import React, { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MessageInputProps {
  onSendMessage: (text: string) => void;
  onTypingStateChange: (hasText: boolean) => void;
  disabled?: boolean;
  // NEW: Snapshot props
  onSnapshotClick?: () => void;
  showSnapshotButton?: boolean;
  isGeneratingSnapshot?: boolean;
}

const MessageInput = ({ 
  onSendMessage, 
  onTypingStateChange, 
  disabled,
  onSnapshotClick,
  showSnapshotButton = false,
  isGeneratingSnapshot = false
}: MessageInputProps) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize the textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
    
    // Notify parent if there is text
    onTypingStateChange(message.length > 0);
    
  }, [message, onTypingStateChange]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
      // Reset height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="p-4 bg-background border-t border-border">
      <div className="max-w-4xl mx-auto relative flex items-end gap-2 bg-card border border-border rounded-xl px-4 py-3 shadow-sm focus-within:ring-1 focus-within:ring-primary/20 focus-within:border-primary/50 transition-all">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Share your thought..."
          disabled={disabled}
          rows={1}
          className="flex-1 bg-transparent border-none focus:ring-0 p-0 pl-1 text-base resize-none max-h-[120px] overflow-y-auto placeholder:text-muted-foreground/50 py-1"
          style={{ minHeight: '24px' }}
        />
        
        {/* Snapshot Button - Shows after 5+ messages with fade-in */}
        {showSnapshotButton && onSnapshotClick && (
          <button
            onClick={onSnapshotClick}
            disabled={isGeneratingSnapshot}
            className="p-2 rounded-lg transition-all duration-200 mb-[1px] bg-stone-100 hover:bg-stone-200 text-stone-700 animate-fade-in"
            title="Generate System Snapshot"
          >
            {isGeneratingSnapshot ? (
              <span className="text-lg animate-pulse">⏳</span>
            ) : (
              <span className="text-lg">📷</span>
            )}
          </button>
        )}
        
        <button
          onClick={() => handleSubmit()}
          disabled={!message.trim() || disabled}
          className={`p-2 rounded-lg transition-all duration-200 mb-[1px] ${
            message.trim() && !disabled
              ? 'bg-primary text-primary-foreground shadow-md hover:scale-105 active:scale-95'
              : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
          }`}
        >
          <Icon name="PaperAirplaneIcon" size={18} />
        </button>
      </div>
    </div>
  );
};

export default MessageInput;